import type { PositionView } from "../portfolio/usePortfolio";
import type { BondSchedule } from "../bond/useBondSchedule";

export type CalEvent = {
  iso: string; // YYYY-MM-DD
  secid: string;
  name: string;
  type: "coupon" | "amort";
  amount: number; // в RUB
  // true, если ставка купона ещё не объявлена эмитентом (плавающий купон)
  // и значение спроецировано по последнему известному. Делаем то же, что
  // карточка на бэке через project_floating_coupons — иначе календарь
  // молча терял будущие месяцы и расходился с «Следующий купон» на карточке.
  projected: boolean;
  // received != null означает «есть контекст позиции и trades» — true/false
  // подсветят чип «зачислено» / «ожидается». Для купонов признак считается по
  // coupon-трейдам, для амортизаций/погашений — по redeem/amort-трейдам.
  received?: boolean | null;
};

// Сводим расписания всех позиций в Map<ISO-дата, события>. Купоны умножаем
// на quantity и пересчитываем в RUB по спот-курсу ЦБ из fxRates. Если курс
// валюты ещё не загружен — фолбэк на buy-time fx, зашитый в позицию.
export function buildEventsByDate(
  positions: PositionView[],
  schedules: Map<string, BondSchedule>,
  fxRates: Record<string, number>,
): Map<string, CalEvent[]> {
  const map = new Map<string, CalEvent[]>();
  for (const v of positions) {
    const sched = schedules.get(v.position.secid);
    if (!sched) continue;
    // Закрытая позиция: текущий quantity = 0, поэтому кол-во берём начальное
    // (initial_quantity), иначе прошлые купоны и погашение схлопнулись бы в
    // ноль. И обрезаем расписание датой закрытия — после продажи/погашения
    // бумаги у нас нет, будущие купоны показывать нельзя. (Для позиции с
    // частичными продажами это приближение: берём начальное кол-во на все
    // прошлые купоны; для типичного «купил–держал–погасил» — точно.)
    const closedIso = v.position.closed_at
      ? v.position.closed_at.slice(0, 10)
      : null;
    const qty = closedIso ? v.initial_quantity : v.position.quantity;
    // Не-RUB: пересчёт по спот-курсу ЦБ из fxRates, фолбэк на buy-time fx,
    // зашитый в позицию, если курс валюты ещё не загружен.
    const fx =
      fxRates[v.currency] ??
      (v.cost_basis !== 0 ? v.cost_basis_rub / v.cost_basis : 1);
    const name = displayName(v);
    const buyDate = v.position.buy_date;

    let lastKnownAmount: number | null = null;
    for (const c of sched.coupons) {
      const realAmount = c.amount;
      const amount = realAmount ?? lastKnownAmount;
      if (amount == null) continue; // ни одного объявленного купона ещё не было
      if (realAmount != null) lastKnownAmount = realAmount;
      if (c.date < buyDate) continue; // купон выплачен до покупки — деньги ушли продавцу
      if (closedIso && c.date > closedIso) continue; // позиция уже закрыта
      pushEvent(map, c.date, {
        iso: c.date,
        secid: v.position.secid,
        name,
        type: "coupon",
        amount: amount * qty * fx,
        projected: realAmount == null,
        received: c.received ?? null,
      });
    }
    for (const a of sched.amortizations) {
      if (a.amount == null) continue;
      if (a.date < buyDate) continue;
      if (closedIso && a.date > closedIso) continue; // позиция уже закрыта
      pushEvent(map, a.date, {
        iso: a.date,
        secid: v.position.secid,
        name,
        type: "amort",
        amount: a.amount * qty * fx,
        projected: false,
        received: a.received ?? null,
      });
    }
  }
  return map;
}

function pushEvent(map: Map<string, CalEvent[]>, iso: string, ev: CalEvent) {
  const cur = map.get(iso);
  if (cur) cur.push(ev);
  else map.set(iso, [ev]);
}

function displayName(v: PositionView): string {
  const src = v.bond_secname ?? v.bond_short_name ?? v.position.secid;
  return src.trim() || v.position.secid;
}

// Сумма всех событий за указанный месяц (year/month — 0-indexed). Для
// заголовка «в этом месяце +X ₽».
export function monthTotal(
  byDate: Map<string, CalEvent[]>,
  year: number,
  month0: number,
): number {
  const prefix = `${year}-${String(month0 + 1).padStart(2, "0")}`;
  let total = 0;
  for (const [iso, events] of byDate) {
    if (!iso.startsWith(prefix)) continue;
    for (const e of events) total += e.amount;
  }
  return total;
}

// Плоский отсортированный список событий за месяц для списка выплат
// под сеткой календаря.
export function monthEvents(
  byDate: Map<string, CalEvent[]>,
  year: number,
  month0: number,
): CalEvent[] {
  const prefix = `${year}-${String(month0 + 1).padStart(2, "0")}`;
  const out: CalEvent[] = [];
  for (const [iso, events] of byDate) {
    if (!iso.startsWith(prefix)) continue;
    for (const e of events) out.push(e);
  }
  out.sort((a, b) => a.iso.localeCompare(b.iso));
  return out;
}

// Раскладываем месяц в массив ячеек, начиная с понедельника недели,
// содержащей 1-е число, и заканчивая воскресеньем недели последнего числа.
// Каждая ячейка либо знает свою дату, либо null (пустая, для дополнения
// сетки до полных недель).
export type CellSpec = {
  iso: string | null;
  day: number | null;
  inMonth: boolean;
};

export function buildMonthCells(year: number, month0: number): CellSpec[] {
  const first = new Date(year, month0, 1);
  const last = new Date(year, month0 + 1, 0); // 0-й день следующего = последний текущего
  // Понедельник = 0, воскресенье = 6
  const startOffset = (first.getDay() + 6) % 7;
  const cells: CellSpec[] = [];
  for (let i = 0; i < startOffset; i++) {
    cells.push({ iso: null, day: null, inMonth: false });
  }
  for (let d = 1; d <= last.getDate(); d++) {
    cells.push({
      iso: isoFor(year, month0, d),
      day: d,
      inMonth: true,
    });
  }
  while (cells.length % 7 !== 0) {
    cells.push({ iso: null, day: null, inMonth: false });
  }
  return cells;
}

function isoFor(year: number, month0: number, day: number): string {
  const m = String(month0 + 1).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
}

export function monthLabel(year: number, month0: number): string {
  return `${MONTHS[month0]} ${year}`;
}

const MONTHS = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];

export const WEEKDAYS = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];
